import {
  CalendarRange,
  ChevronDown,
  ChevronRight,
  CreditCard,
  Download,
  FileText,
  LayoutDashboard,
  Menu,
  MessageCircle,
  Printer,
  ClipboardPlus as ReportIcon,
  User2Icon as UserIcon,
  X
} from 'lucide-react'
import { useContext, useEffect, useRef, useState } from 'react'
import { FaLock } from 'react-icons/fa6'
import { Link, useLocation } from 'react-router-dom'
import { toast } from 'sonner'

import PlanUpgradeDialog from '@/components/ui/plan-upgrade-dialog'
import { AuthContext } from '../../../contexts/auth-context'
import { generateQrCodeService } from '../../../services'
import logger from '../../../utils/logger'
import { Button } from '../../ui/button'

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation()
  const { auth } = useContext(AuthContext)
  const plan = auth?.user?.subscription?.planTier || 'basic'
  const [loading, setLoading] = useState(false)
  const [newQRCode, setNewQRCode] = useState('')
  const [showPlanUpgrade, setShowPlanUpgrade] = useState(false)
  const [planUpgrade, setPlanUpgrade] = useState({
    name: '',
    desc: '',
  })
  const [isMobile, setIsMobile] = useState(false)
  const [expandedItems, setExpandedItems] = useState({}) // Track expanded navigation items
  const qrCodeRef = useRef(null)
  const sidebarRef = useRef(null)
  const user = auth.user

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024) // Standard breakpoint for lg
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Handle click outside to close sidebar on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        toggleSidebar()
      }
    }

    if (isMobile && isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [isMobile, isOpen, toggleSidebar])

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobile) {
      if (isOpen) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = 'unset'
      }
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobile, isOpen])

  // Enhanced navigation items with subsections
  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/business/dashboard',
      icon: LayoutDashboard,
      available: true,
    },

    {
      name: 'Forms',
      href: '/business/my-forms',
      icon: FileText,
      available: true,
      // subItems: [
      //   { name: 'All Forms', href: '/business/my-forms' },
      // { name: 'Create Form', href: '/business/my-forms/create' },
      // { name: 'Templates', href: '/business/my-forms/templates' },
      // { name: 'Form Analytics', href: '/business/my-forms/analytics' },
      // ],
    },
    {
      name: 'Cards',
      href: '/business/my-cards',
      icon: CreditCard,
      available: true,
      // subItems: [
      //   { name: 'All Cards', href: '/business/my-cards' },
      //   // { name: 'Create Card', href: '/business/my-cards/create' },
      //   // { name: 'Card Templates', href: '/business/my-cards/templates' },
      //   // { name: 'Card Settings', href: '/business/my-cards/settings' },
      // ],
    },
    {
      name: 'Subscription',
      href: '/business/profile/plans',
      icon: CalendarRange,
      available: true,
      // subItems: [
      //   { name: 'Current Plan', href: '/business/profile/plans' },
      //   // { name: 'Billing History', href: '/business/profile/billing' },
      //   // { name: 'Payment Methods', href: '/business/profile/payment-methods' },
      //   // { name: 'Upgrade Plan', href: '/business/profile/upgrade' },
      // ],
    },
    {
      name: 'Message',
      href: '/business/message',
      icon: MessageCircle,
      // subItems: [
      //   { name: 'Current Plan', href: '/business/profile/plans' },
      //   // { name: 'Billing History', href: '/business/profile/billing' },
      //   // { name: 'Payment Methods', href: '/business/profile/payment-methods' },
      //   // { name: 'Upgrade Plan', href: '/business/profile/upgrade' },
      // ],
      available: plan?.toLowerCase() !== 'basic', // Locked for Basic
    },
    {
      name: 'Customers',
      href: '/business/my-customers',
      icon: UserIcon,
      available: true,
      // subItems: [
      //   { name: 'All Customers', href: '/business/my-customers' },
      //   // { name: 'Customer Groups', href: '/business/my-customers/groups' },
      //   // { name: 'Customer Analytics', href: '/business/my-customers/analytics' },
      //   // { name: 'Import Customers', href: '/business/my-customers/import' },
      // ],
    },
    {
      name: 'Reports',
      href: '/business/reports/customer/demographics',
      icon: ReportIcon,
      subItems: [
        { name: 'Demographics', href: '/business/reports/customer/demographics' },
        { name: 'Activity', href: '/business/reports/customer/activity' },
        { name: 'Loyal Customer', href: '/business/reports/customer/loyal' },
        { name: 'Purchase Trends', href: '/business/reports/customer/purchase' },
      ],
      available: true,
    },
  ]

  const isActive = (path) => location.pathname.startsWith(path)

  const isParentActive = (item) => {
    if (isActive(item.href)) return true
    if (item.subItems) {
      return item.subItems.some((subItem) => isActive(subItem.href))
    }
    return false
  }

  const toggleExpanded = (itemName) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemName]: !prev[itemName],
    }))
  }

  // Auto-expand parent items if a sub-item is active
  useEffect(() => {
    const newExpandedItems = {}
    navigationItems.forEach((item) => {
      if (item.subItems && item.subItems.some((subItem) => isActive(subItem.href))) {
        newExpandedItems[item.name] = true
      }
    })
    setExpandedItems((prev) => ({ ...prev, ...newExpandedItems }))
  }, [location.pathname])

  const handleGenerateQRCode = async () => {
    try {
      setLoading(true)
      const data = await generateQrCodeService()

      if (data?.data) {
        setNewQRCode(data.data)
      }
      toast.success(data?.message || 'QR code generated successfully!')
    } catch (error) {
      toast.error('Failed to generate QR code')
      logger.error('Error generating QR code:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePrintQR = () => {
    const qrCodeImage = newQRCode || user?.qrCode
    if (!qrCodeImage) {
      toast.error('No QR code available to print')
      return
    }

    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Print QR Code</title>
            <style>
              body { display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0; }
              img { max-width: 300px; max-height: 300px; width: auto; height: auto; }
            </style>
          </head>
          <body>
            <img src="${qrCodeImage}" alt="QR Code" />
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  window.close();
                }, 500);
              };
            </script>
          </body>
        </html>
      `)
      printWindow.document.close()
    } else {
      toast.error('Could not open print window. Please disable pop-up blocker.')
    }
  }

  const handleDownloadQR = () => {
    const qrCodeImage = newQRCode || user?.qrCode
    if (!qrCodeImage) {
      toast.error('No QR code available to download')
      return
    }

    const link = document.createElement('a')
    link.href = qrCodeImage
    link.download = 'qr-code.png'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success('QR code download initiated')
  }

  const handleNavItemClick = () => {
    if (isMobile && isOpen) {
      toggleSidebar()
    }
  }

  const dummyQRCode = 'https://api.qrserver.com/v1/create-qr-code/?data=DummyQR&size=200x200'

  return (
    <>
      {/* Mobile left-side hamburger menu button */}
      {isMobile && !isOpen && (
        <button
          onClick={toggleSidebar}
          className="fixed bottom-6 left-6 z-50 bg-black text-white rounded-full p-3 shadow-lg hover:bg-gray-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
      )}

      {/* Mobile overlay */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-transparent bg-opacity-50 z-40 transition-opacity duration-200"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      <aside
        ref={sidebarRef}
        className={`bg-white border-r border-gray-200 text-gray-800 transition-all duration-300 ease-in-out flex flex-col z-50 ${
          isMobile
            ? `fixed top-0 left-0 h-full  ${
                isOpen ? 'w-64 translate-x-0' : 'w-64 -translate-x-full'
              }`
            : `sticky top-0 h-screen  ${isOpen ? 'w-64' : 'w-16'}`
        }`}
        aria-label="Sidebar"
      >
        {/* Sidebar header with toggle button */}
        <div
          className={`flex items-center p-4 border-b border-gray-200 ${
            isMobile ? 'justify-between bg-[#4C2859]' : 'justify-end bg-white'
          }`}
        >
          {isMobile && isOpen && <h2 className="text-lg font-semibold text-white">Menu</h2>}
          <button
            onClick={toggleSidebar}
            className={`flex items-center justify-center hover:bg-opacity-20 rounded-lg p-2 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${
              isMobile
                ? 'text-white hover:bg-gray-100'
                : `text-[#4C2859] hover:bg-gray-100 ${isOpen ? '' : 'w-full'}`
            }`}
            aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {isMobile ? (
              <X size={24} />
            ) : isOpen ? (
              <ChevronRight className="transform rotate-180" size={20} />
            ) : (
              <ChevronRight size={20} />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col flex-1 py-4 overflow-y-auto">
          {navigationItems.map((item) => {
            const Icon = item.icon
            const hasSubItems = item.subItems && item.subItems.length > 0
            const isExpanded = expandedItems[item.name]
            const isItemActive = isParentActive(item)
            const isAvailable = item.available

            return (
              <div key={item.name} className="mx-2">
                {/* Main navigation item */}
                <div className="flex items-center relative">
                  <Link
                    to={isAvailable ? item.href : '#'}
                    onClick={() => {
                      handleNavItemClick(item)
                      if (!isAvailable) {

                        setPlanUpgrade(()=>{
                          if (item.name === 'Message') {
                            return {
                              name: "Messaging",
                              desc: `Send push notifications to your customers and enhance engagement.`,
                            }
                          }
                          if (item.name === 'Reports') {
                            return {
                              name: "Analytics & Reports",
                              desc: `Gain insights into customer demographics, activity,purchase trends and more.`,
                            }
                          }
                          return {
                            name:"",
                            desc:"",
                          }
                        })



                        setShowPlanUpgrade(true)
                      }
                    }}
                    className={`flex items-center px-4 py-3 rounded-lg transition-colors flex-1 ${
                      isItemActive && isAvailable
                        ? 'bg-[#4C2859] text-white'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-[#4C2859]'
                    } ${!isOpen && !isMobile ? 'justify-center' : ''} ${isMobile ? 'min-h-[48px]' : ''}
                   `}
                    title={isOpen ? '' : item.name}
                  >
                    <Icon
                      size={isOpen || isMobile ? 20 : 22}
                      className={`${
                        isItemActive && isAvailable
                          ? isOpen || isMobile
                            ? ''
                            : 'text-white'
                          : 'text-[#4C2859]'
                      } transition-all duration-200 flex-shrink-0`}
                    />
                    {isOpen && (
                      <span className="ml-3 font-medium text-sm lg:text-base">{item.name}</span>
                    )}
                    {!isAvailable && <span className="text-2xl justify-end">🔒</span>}
                  </Link>

                  {/* Expand/Collapse button for items with subitems */}
                  {hasSubItems && isOpen && isAvailable && (
                    <button
                      onClick={() => toggleExpanded(item.name)}
                      className={`p-2 rounded-lg transition-colors ${
                        isItemActive
                          ? 'text-[#4C2859] hover:bg-white hover:bg-opacity-20'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      aria-label={isExpanded ? 'Collapse submenu' : 'Expand submenu'}
                    >
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${
                          isExpanded ? 'transform rotate-180' : ''
                        }`}
                      />
                    </button>
                  )}
                </div>

                {/* Sub-navigation items */}
                {hasSubItems && isOpen && isExpanded && isAvailable && (
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-4">
                    {item.subItems.map((subItem) => (
                      <Link
                        key={subItem.name}
                        to={subItem.href}
                        onClick={handleNavItemClick}
                        className={`block px-3 py-2 rounded-lg transition-colors text-sm ${
                          isActive(subItem.href)
                            ? 'text-[#4C2859] bg-white font-bold'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-[#4C2859]'
                        }`}
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        {/* QR Code section */}
        {isOpen && (
          <div className="p-4 lg:p-6 mt-auto border-t border-gray-200 bg-[#f7f8fc]">
            <p className="text-sm mb-3 text-gray-800 font-semibold text-center lg:text-left">
              Your QR Code
            </p>

            <div className="flex flex-col gap-3 lg:gap-4 items-center justify-center">
              {loading ? (
                <div className="w-full h-32 lg:h-40 flex items-center justify-center mb-2">
                  <div className="w-8 h-8 lg:w-10 lg:h-10 border-4 border-[#4C2859] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : user?.qrCode || newQRCode ? (
                <div ref={qrCodeRef} className="relative w-32 h-32 lg:w-40 lg:h-40">
                  <img
                    src={newQRCode || user?.qrCode}
                    alt="User QR Code"
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="relative w-32 h-32 lg:w-40 lg:h-40">
                  <img
                    src={dummyQRCode}
                    alt="Dummy QR Code"
                    className="w-full h-full blur-sm object-contain rounded-xl"
                  />
                  <FaLock
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[#4C2859]"
                    size={isMobile ? 24 : 32}
                  />
                </div>
              )}

              <Button
                onClick={handleGenerateQRCode}
                disabled={loading}
                className="cursor-pointer w-full text-[#4C2859] bg-gray-200 hover:text-white hover:bg-[#4C2859] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 ease-in-out rounded-xl shadow-md shadow-gray-300 px-4 py-2 text-sm min-h-[40px]"
              >
                {loading
                  ? 'Loading...'
                  : user?.qrCode || newQRCode
                    ? 'Regenerate QR'
                    : 'Generate QR'}
              </Button>

              {(user?.qrCode || newQRCode) && (
                <div className="flex w-full gap-2 mt-2">
                  <Button
                    onClick={handlePrintQR}
                    className="cursor-pointer flex-1 text-[#4C2859] bg-gray-200 hover:text-white hover:bg-[#4C2859] transition-all duration-300 ease-in-out rounded-xl shadow-md shadow-gray-300 px-2 py-2 text-sm min-h-[40px] flex items-center justify-center"
                  >
                    <Printer size={14} className="mr-1 flex-shrink-0" />
                    <span className="hidden sm:inline">Print</span>
                    <span className="sm:hidden">Print</span>
                  </Button>
                  <Button
                    onClick={handleDownloadQR}
                    className="cursor-pointer flex-1 text-[#4C2859] bg-gray-200 hover:text-white hover:bg-[#4C2859] transition-all duration-300 ease-in-out rounded-xl shadow-md shadow-gray-300 px-2 py-2 text-sm min-h-[40px] flex items-center justify-center"
                  >
                    <Download size={14} className="mr-1 flex-shrink-0" />
                    <span className="hidden sm:inline">Download</span>
                    <span className="sm:hidden">Save</span>
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </aside>

      {/* Plan Upgrade Dialog */}
      <PlanUpgradeDialog
        showPlanUpgrade={showPlanUpgrade}
        setShowPlanUpgrade={setShowPlanUpgrade}
        name={planUpgrade.name}
        desc={planUpgrade.desc}
      />
    </>
  )
}

export default Sidebar
